import { isAuthorized } from 'services';
import { DashboardCardConfig } from '../types';
import { DEFAULT_CARD_CONFIGS } from '../config/cardConfigs';
import { LkHomeDashboard } from 'generated/openApi';

export class CardConfigService {
    private static readonly STORAGE_KEY = 'dashboard-card-configs';

    static initialize(): DashboardCardConfig[] {
        const stored = localStorage.getItem(this.STORAGE_KEY);
        if (!stored) {
            return DEFAULT_CARD_CONFIGS;
        }
        try {
            const storedConfigs = JSON.parse(stored) as Partial<DashboardCardConfig>[];

            // Map valid stored configs for fast lookup
            const storedMap = new Map(storedConfigs
                .filter(c => c && c.id != null)
                .map(c => [c.id!, c])
            );

            // Master List: Iterate defaults to ensure code updates (permissions/icons) always win
            const mergedConfigs = DEFAULT_CARD_CONFIGS.map(defaultCard => {
                const userCard = storedMap.get(defaultCard.id);

                if (!userCard) {
                    return defaultCard;
                }

                //If default config is marked as LOCKED force it to be ENABLED.
                //this overrides any 'disabled' state user might have saved in local storage.
                const isLocked = defaultCard.locked;

                //Keep code-defined props, but respect user's enabled/order preference
                return {
                    ...defaultCard,
                    // If locked, force true. Otherwise, use user preference (defaulting to code config)
                    enabled: isLocked ? true : (userCard.enabled ?? defaultCard.enabled),
                    order: userCard.order ?? defaultCard.order
                };
            });

            // Only write to storage if the result actually differs from what was there
            const mergedString = JSON.stringify(mergedConfigs);
            if (stored !== mergedString) {
                localStorage.setItem(this.STORAGE_KEY, mergedString);
            }

            return mergedConfigs;
        } catch (error) {
            console.error('Failed to parse stored card configs:', error);
            return DEFAULT_CARD_CONFIGS;
        }
    }

    static save(cards: DashboardCardConfig[]): void {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(cards));
    }

    static toggleCard(cards: DashboardCardConfig[], cardId: string): DashboardCardConfig[] {
        return cards.map((card) =>
            //safety check: do not toggle if card is locked
            card.id === cardId && !card.locked
                ? { ...card, enabled: !card.enabled }
                : card
        );
    }
    
    // Check if user has dashboard access for card
    private static hasDashboardAccess(
        card: DashboardCardConfig,
        dashboards: LkHomeDashboard[] | undefined
    ): boolean {
        if (!card.dashboardId) {
            return true;
        }
        return dashboards?.some((d) => d.id === card.dashboardId) ?? false;
    }

    // Check if user has required permissions for card
    private static hasRequiredPermissions(card: DashboardCardConfig): boolean {
        if (!card.requiredPermissions || card.requiredPermissions.length === 0) {
            return true;
        }
        return card.requiredPermissions.every((req) =>
            isAuthorized(req.permission, req.action)
        );
    }

    // Filter cards by authorization (dashboard access + permissions)
    static filterByAuthorization(
        cards: DashboardCardConfig[],
        dashboards: LkHomeDashboard[] | undefined
    ): DashboardCardConfig[] {
        return cards.filter((card) => {
            const hasDashboard = this.hasDashboardAccess(card, dashboards);
            const hasPermissions = this.hasRequiredPermissions(card);
            return hasDashboard && hasPermissions;
        });
    }

    static getVisibleCards(
        cards: DashboardCardConfig[],
        dashboards: LkHomeDashboard[] | undefined
    ): DashboardCardConfig[] {
        return this.filterByAuthorization(cards, dashboards)
            .filter((card) => card.enabled)
            .sort((a, b) => a.order - b.order);
    }
}